import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    X, Sparkles, Loader2, Check, Coffee, Sun, Sunset, Apple,
    ChevronUp, Send
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ClearableInput } from './ui/clearable-input';
import { MemorySuggestions } from './MemorySuggestions';
import { PantryQuickPicks } from './PantryQuickPicks';
import NanoukEstimateBreakdown from './NanoukEstimateBreakdown';
import { toast } from 'sonner';
import { VoiceInput } from './VoiceInput';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const mealTypeConfig = {
    breakfast: { icon: Coffee, label: { de: 'Frühstück', en: 'Breakfast' }, color: 'text-amber-400' },
    lunch: { icon: Sun, label: { de: 'Mittagessen', en: 'Lunch' }, color: 'text-yellow-400' },
    dinner: { icon: Sunset, label: { de: 'Abendessen', en: 'Dinner' }, color: 'text-orange-400' },
    snack: { icon: Apple, label: { de: 'Snack', en: 'Snack' }, color: 'text-lime-400' },
};

export function QuickLogDrawer({ 
    isOpen, 
    onClose, 
    selectedDate, 
    onSuccess,
    type = 'food' // 'food', 'water', etc.
}) {
    const { language } = useLanguage();
    const [input, setInput] = useState('');
    const [mealType, setMealType] = useState('lunch');
    // Where the meal was eaten — feeds Nanouk personalisation + future
    // social-trend analysis (e.g. "you ate out 4× this week").
    const [diningContext, setDiningContext] = useState('home');
    const [analyzing, setAnalyzing] = useState(false);
    const [analyzed, setAnalyzed] = useState(false);
    const [result, setResult] = useState(null);
    const [interimTranscript, setInterimTranscript] = useState('');
    
    const handleAnalyze = async () => {
        if (!input.trim()) {
            toast.error(language === 'de' ? 'Bitte gib etwas ein' : 'Please enter something');
            return;
        }
        
        setAnalyzing(true);
        try {
            const response = await axios.post(`${API}/nutrition/analyze`, {
                food_description: input,
                meal_type: mealType,
                portion_info: null
            });
            setResult(response.data);
            setAnalyzed(true);
        } catch (error) {
            toast.error(language === 'de' ? 'Analyse fehlgeschlagen' : 'Analysis failed');
        } finally {
            setAnalyzing(false);
        }
    };
    
    const handleSave = async () => {
        if (!result) return;
        
        try {
            await axios.post(`${API}/nutrition/food`, {
                name: result.name,
                meal_type: mealType,
                calories: result.calories,
                protein: result.protein,
                carbs: result.carbs,
                fat: result.fat,
                fiber: result.fiber || 0,
                dining_context: diningContext,
                logged_at: selectedDate.toISOString()
            });
            
            toast.success(
                <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span>{language === 'de' ? 'Gespeichert! +5 XP' : 'Saved! +5 XP'}</span>
                </div>
            );
            
            handleClose();
            onSuccess?.();
        } catch (error) {
            toast.error(language === 'de' ? 'Fehler beim Speichern' : 'Failed to save');
        }
    };
    
    const handleClose = () => {
        setInput('');
        setMealType('lunch');
        setAnalyzing(false);
        setAnalyzed(false);
        setResult(null);
        onClose();
    };
    
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !analyzing) {
            if (analyzed) {
                handleSave();
            } else {
                handleAnalyze();
            }
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
                        onClick={handleClose}
                    />
                    
                    {/* Drawer */}
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="fixed bottom-0 left-0 right-0 z-50 bg-card rounded-t-3xl border-t border-border shadow-2xl max-h-[85vh] overflow-y-auto"
                        style={{ maxHeight: '85vh' }}
                    >
                        {/* Handle */}
                        <div className="flex justify-center py-3">
                            <div className="w-12 h-1.5 bg-muted rounded-full" />
                        </div>
                        
                        <div className="px-4 pb-32 space-y-2">
                            {/* Header */}
                            <div className="flex items-center justify-between">
                                <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
                                    <Sparkles className="w-5 h-5 text-lime-400" />
                                    {language === 'de' ? 'Quick Log' : 'Quick Log'}
                                </h3>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 rounded-full"
                                    onClick={handleClose}
                                >
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                            
                            {/* Date indicator */}
                            <div className="text-center py-1.5 px-3 rounded-xl bg-primary/10 text-primary text-sm font-medium">
                                {selectedDate.toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
                                    weekday: 'long',
                                    day: 'numeric',
                                    month: 'long',
                                    year: 'numeric'
                                })}
                            </div>
                            
                            {/* Meal Type Selector */}
                            <div className="flex gap-2 justify-center">
                                {Object.entries(mealTypeConfig).map(([type, config]) => {
                                    const Icon = config.icon;
                                    return (
                                        <button
                                            key={type}
                                            onClick={() => setMealType(type)}
                                            className={`
                                                flex flex-col items-center gap-1 px-3 py-2 rounded-xl transition-all
                                                ${mealType === type 
                                                    ? 'bg-secondary ring-2 ring-cyan-500' 
                                                    : 'bg-card hover:bg-muted/50'
                                                }
                                            `}
                                        >
                                            <Icon className={`w-5 h-5 ${config.color}`} />
                                            <span className="text-xs text-muted-foreground">
                                                {config.label[language]}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Dining Context — where was this meal eaten?
                                Used by Nanouk for social/context awareness. */}
                            <div className="space-y-1.5">
                                <p className="text-[10px] uppercase tracking-wide text-muted-foreground text-center">
                                    {language === 'de' ? 'Wo gegessen?' : 'Where eaten?'}
                                </p>
                                <div className="flex flex-wrap gap-1.5 justify-center" data-testid="dining-context-picker">
                                    {[
                                        { v: 'home', de: '🏠 Zuhause', en: '🏠 Home' },
                                        { v: 'restaurant', de: '🍽️ Restaurant', en: '🍽️ Restaurant' },
                                        { v: 'friends', de: '👥 Freunde', en: '👥 Friends' },
                                        { v: 'takeaway', de: '🥡 Take-Away', en: '🥡 Take-away' },
                                        { v: 'work', de: '💼 Arbeit', en: '💼 Work' },
                                        { v: 'travel', de: '✈️ Reise', en: '✈️ Travel' },
                                    ].map(opt => (
                                        <button
                                            key={opt.v}
                                            type="button"
                                            onClick={() => setDiningContext(opt.v)}
                                            data-testid={`dining-context-${opt.v}`}
                                            className={`text-[11px] px-2.5 py-1 rounded-full transition-colors ${
                                                diningContext === opt.v
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                                            }`}
                                        >
                                            {opt[language] || opt.de}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Pantry Quick-Picks — tap to log scanned/saved foods instantly */}
                            <PantryQuickPicks
                                mealType={mealType}
                                limit={12}
                                smart
                                onLogged={onSuccess}
                            />

                            {/* Input Area */}
                            {!analyzed ? (
                                <div className="space-y-3">
                                    <div className="flex gap-2">
                                        <ClearableInput
                                            value={input}
                                            onChange={(e) => setInput(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            placeholder={language === 'de' 
                                                ? "Was hast du gegessen? z.B. Pasta mit Tomatensauce" 
                                                : "What did you eat? e.g. Pasta with tomato sauce"
                                            }
                                            className="h-12 text-base bg-secondary border-border"
                                            autoFocus
                                            data-testid="quick-log-input"
                                            clearTestId="quick-log-input-clear"
                                        />
                                        <VoiceInput
                                            onResult={(text) => {
                                                setInput(prev => {
                                                    const sep = prev && !prev.endsWith(' ') ? ' ' : '';
                                                    return (prev + sep + text).trimStart();
                                                });
                                                setInterimTranscript('');
                                            }}
                                            onInterim={setInterimTranscript}
                                            testid="quick-log-voice-btn"
                                            className="h-12 w-12 shrink-0"
                                        />
                                    </div>
                                    {/* Live voice transcription preview */}
                                    {interimTranscript && (
                                        <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 text-xs" data-testid="quick-log-voice-interim">
                                            <span className="inline-flex h-2 w-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" aria-hidden />
                                            <span className="text-red-900 dark:text-red-200 italic truncate">{interimTranscript}</span>
                                        </div>
                                    )}
                                    
                                    {/* Memory suggestions from previous meals */}
                                    <MemorySuggestions 
                                        category="food"
                                        currentValue={input}
                                        onSelect={(s) => setInput(s.name)}
                                        className="mt-1"
                                    />
                                    
                                    <Button
                                        onClick={handleAnalyze}
                                        disabled={analyzing || !input.trim()}
                                        className="w-full h-12 bg-gradient-to-r from-lime-500 to-emerald-500 hover:from-lime-600 hover:to-emerald-600 text-foreground font-semibold rounded-xl"
                                        data-testid="analyze-btn"
                                    >
                                        {analyzing ? (
                                            <>
                                                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                                {language === 'de' ? 'Analysiere...' : 'Analyzing...'}
                                            </>
                                        ) : (
                                            <>
                                                <Sparkles className="w-5 h-5 mr-2" />
                                                {language === 'de' ? 'Nährwerte berechnen' : 'Calculate Nutrients'}
                                            </>
                                        )}
                                    </Button>
                                </div>
                            ) : (
                                /* Result View */
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="space-y-4"
                                >
                                    <div className="bg-card rounded-2xl p-4 border border-border">
                                        <h4 className="font-semibold text-foreground mb-3">{result.name}</h4>
                                        <div className="grid grid-cols-4 gap-3">
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-orange-400">{result.calories}</div>
                                                <div className="text-xs text-muted-foreground">kcal</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-red-400">{result.protein}g</div>
                                                <div className="text-xs text-muted-foreground">{language === 'de' ? 'Protein' : 'Protein'}</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-amber-400">{result.carbs}g</div>
                                                <div className="text-xs text-muted-foreground">{language === 'de' ? 'Kohlenh.' : 'Carbs'}</div>
                                            </div>
                                            <div className="text-center">
                                                <div className="text-2xl font-bold text-yellow-400">{result.fat}g</div>
                                                <div className="text-xs text-muted-foreground">{language === 'de' ? 'Fett' : 'Fat'}</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Transparent breakdown of Nanouk's estimation — collapsible & inline-editable */}
                                    <NanoukEstimateBreakdown
                                        result={result}
                                        language={language}
                                        onChange={setResult}
                                    />
                                    
                                    <div className="flex gap-3">
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setAnalyzed(false);
                                                setResult(null);
                                            }}
                                            className="flex-1 h-12 rounded-xl"
                                        >
                                            {language === 'de' ? 'Ändern' : 'Edit'}
                                        </Button>
                                        <Button
                                            onClick={handleSave}
                                            className="flex-1 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 rounded-xl"
                                            data-testid="save-btn"
                                        >
                                            <Check className="w-5 h-5 mr-2" />
                                            {language === 'de' ? 'Speichern' : 'Save'}
                                        </Button>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
